import React from 'react';

import { ArrayFieldTemplateProps, RJSFSchema } from '@rjsf/utils';

import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack, Typography } from '@mui/material';

/**
 * SimpleArrayFieldTemplate - A simplified RJSF ArrayFieldTemplate without
 * the heavy Paper/box-shadow styling of the default MUI template.
 *
 * Features:
 * - Clean, minimal styling
 * - Add/Remove/Move buttons (handled by ArrayFieldItemTemplate)
 * - No Paper wrapper or box shadows
 *
 * Note: In RJSF v6, items are React elements that include their own controls.
 * The template just needs to render them in the desired layout.
 */

export default function SimpleArrayFieldTemplate({
  items,
  canAdd,
  onAddClick,
  title,
  schema,
  uiSchema,
  disabled,
  readonly,
}: ArrayFieldTemplateProps<unknown, RJSFSchema>) {
  const roleLayout = Boolean(uiSchema?.['ui:options']?.roleLayout);
  return (
    <Box
      sx={{
        my: uiSchema?.['ui:options']?.compact ? 0 : 4,
        maxWidth: roleLayout ? 760 : undefined,
        ...(roleLayout && { '--role-actions-width': (theme) => theme.spacing(13) }),
      }}
    >
      {title && (
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
          {title}
        </Typography>
      )}

      {schema.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {schema.description}
        </Typography>
      )}

      {roleLayout && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Roles are listed in order. Use the arrows to change their order.
        </Typography>
      )}

      <Stack spacing={1}>
        {items.map((item) => (
          <React.Fragment key={item.key}>{item}</React.Fragment>
        ))}
      </Stack>

      {canAdd && (
        <Box
          sx={
            roleLayout
              ? {
                  display: 'flex',
                  justifyContent: 'center',
                  pr: (theme) => `calc(var(--role-actions-width) + ${theme.spacing(1)})`,
                }
              : undefined
          }
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddClick}
            disabled={disabled || readonly}
            sx={{ mt: 1, ...(roleLayout && { bgcolor: 'background.paper' }) }}
          >
            Add {schema.items?.['title'] || 'item'}
          </Button>
        </Box>
      )}
    </Box>
  );
}
